'use client';

import {
  DynamicApplicationScanSettingsCard,
  toasterApi,
  type DynamicApplicationScanRunSummary,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { useApplicationById } from '../../../hooks/api/useApplicationById';
import { useStopDynamicApplicationScan } from '../../../hooks/api/useStopDynamicApplicationScan';
import { useTriggerDynamicApplicationScan } from '../../../hooks/api/useTriggerDynamicApplicationScan';
import { useUpdateApplicationFields } from '../../../hooks/api/useUpdateApplicationFields';
import { AdaptivePausedBadge } from '../AdaptivePausedBadge';
import {
  disableDynamicApplicationScanDraft,
  enableDynamicApplicationScanDraft,
  type DynamicApplicationScanBaseline,
  type DynamicApplicationScanDraft,
} from './dynamicApplicationScanDraft';

type DynamicApplicationScanConfigurationCardProps = {
  applicationId: string;
  value: DynamicApplicationScanDraft;
  baseline: DynamicApplicationScanBaseline | null;
  activeRun?: DynamicApplicationScanRunSummary | null;
  lastRun?: DynamicApplicationScanRunSummary | null;
  disabled?: boolean;
  isReadOnly?: boolean;
  hasUnsavedChanges?: boolean;
  onChange: (next: DynamicApplicationScanDraft) => void;
};

const toErrorTitle = ({
  error,
  fallback,
}: {
  error: unknown;
  fallback: string;
}): string =>
  error instanceof Error && error.message.length > 0 ? error.message : fallback;

export function DynamicApplicationScanConfigurationCard({
  applicationId,
  value,
  baseline,
  activeRun,
  lastRun,
  disabled,
  isReadOnly,
  hasUnsavedChanges,
  onChange,
}: DynamicApplicationScanConfigurationCardProps) {
  const t = useTranslations('designSystem.automation.dynamicApplicationScan');
  const { application } = useApplicationById({ id: applicationId });
  const { mutateAsync: updateApplication, isPending: isRpmPending } =
    useUpdateApplicationFields();
  const { mutateAsync: triggerScan, isPending: isTriggerPending } =
    useTriggerDynamicApplicationScan({ applicationId });
  const { mutateAsync: stopScan, isPending: isStopPending } =
    useStopDynamicApplicationScan({ applicationId });

  const handleRequestsPerMinuteCommit = useCallback(
    async (next: number | null) => {
      try {
        await updateApplication({
          applicationId,
          body: { targetRequestsPerMinute: next },
        });

        return true;
      } catch (error: unknown) {
        toasterApi.error({
          title: t('saveError'),
          description:
            error instanceof Error && error.message.length > 0
              ? error.message
              : undefined,
        });

        return false;
      }
    },
    [applicationId, t, updateApplication],
  );
  const handleRunNow = useCallback(async () => {
    try {
      await triggerScan();
    } catch (error: unknown) {
      toasterApi.error({
        title: toErrorTitle({ error, fallback: t('runNow.error') }),
      });
    }
  }, [triggerScan, t]);
  const handleStop = useCallback(async () => {
    try {
      await stopScan();
    } catch (error: unknown) {
      toasterApi.error({
        title: toErrorTitle({ error, fallback: t('runNow.stopError') }),
      });
    }
  }, [stopScan, t]);

  return (
    <DynamicApplicationScanSettingsCard
      banner={<AdaptivePausedBadge applicationId={applicationId} />}
      vulnerabilityEnabled={value.vulnerabilityEnabled}
      directoryEnabled={value.directoryEnabled}
      requestsPerMinute={application?.targetRequestsPerMinute}
      isScheduleOpen={value.enabled}
      scheduleRule={value.scheduleRule}
      nextRunAt={baseline?.nextRunAt}
      activeRunStatus={activeRun?.status}
      lastRun={lastRun}
      hasUrl={baseline?.hasUrl}
      hasScanTargets={baseline?.hasScanTargets}
      disabled={Boolean(disabled || isReadOnly)}
      isReadOnly={isReadOnly}
      isRunNowBlocked={hasUnsavedChanges}
      isRequestsPerMinuteSaving={isRpmPending}
      isTriggering={isTriggerPending}
      isStopping={isStopPending}
      onVulnerabilityEnabledChange={(vulnerabilityEnabled) =>
        onChange({ ...value, vulnerabilityEnabled })
      }
      onDirectoryEnabledChange={(directoryEnabled) =>
        onChange({ ...value, directoryEnabled })
      }
      onRequestsPerMinuteCommit={handleRequestsPerMinuteCommit}
      onScheduleEnabledChange={(checked) =>
        onChange(
          checked
            ? enableDynamicApplicationScanDraft(value)
            : disableDynamicApplicationScanDraft(value),
        )
      }
      onScheduleRuleChange={(scheduleRule) =>
        onChange({ ...value, scheduleRule })
      }
      onRunNow={() => void handleRunNow()}
      onStop={() => void handleStop()}
    />
  );
}
